import React, { useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { tierCan } from "../../auth/permissions";
import { allSplitsFor, ownSplitFor } from "../../data/BookDataProvider";
import { COMMISSION_RATE, formatDate, formatMoney } from "../../data/format";
import { Badge, Button, Drawer } from "../../ui";
import { PolicyStatusBadge, RenewalBadge } from "../shared";
import { renewalInfo } from "../../data/format";
import PolicyEditDrawer from "./PolicyEditDrawer";
import PolicyStatusAction from "./PolicyStatusAction";

/**
 * Policy detail drawer (§3.8): grouped, labeled, formatted coverages —
 * "Dwelling $429,200", never raw integers.
 *
 * Tier projection (D2, UX mirror of Session H's backend projection): a
 * referral agent sees line/carrier/status/dates/premium + THEIR OWN split;
 * coverages and other participants' splits are staff-only. Per Session E's
 * direction the referral view reads as complete, not redacted — the split
 * chip is the row's reward, and no disabled/locked affordances appear.
 */

export const COVERAGE_FIELDS = {
  auto: [
    ["BI", "Bodily injury"],
    ["PD", "Property damage"],
    ["UM", "Uninsured motorist"],
    ["UIM", "Underinsured motorist"],
    ["TORT", "Tort option"]
  ],
  home: [
    ["address", "Insured address"],
    ["dwelling", "Dwelling"],
    ["structures", "Other structures"],
    ["persprop", "Personal property"],
    ["lossofuse", "Loss of use"],
    ["liability", "Liability"],
    ["medical", "Medical"],
    ["deductible", "Deductible"]
  ],
  umbrella: [["liability", "Liability limit"]]
};

const MONEYISH = new Set([
  "dwelling",
  "structures",
  "persprop",
  "lossofuse",
  "liability",
  "medical",
  "deductible"
]);

function KvRow({ label, children }) {
  return (
    <div className="feat-kv__row">
      <span className="feat-kv__key">{label}</span>
      <span className="feat-kv__value">{children}</span>
    </div>
  );
}

function PolicyDrawer({ policy, onClose, onSaved }) {
  const { tier, agents, identity } = useAuth();
  const can = tierCan(tier);
  const [editing, setEditing] = useState(false);
  // A local status override so a Mark-inactive/Reactivate reflects instantly
  // in the open drawer (the badge flips, the button becomes its inverse =
  // undo) even before the book refresh re-flows the policy prop.
  const [statusOverride, setStatusOverride] = useState(null);

  if (!policy) {
    return null;
  }

  const shown = statusOverride && statusOverride._id === policy._id ? statusOverride : policy;
  const renewal = renewalInfo(shown);
  const coverage = (COVERAGE_FIELDS[shown.lobKey] || []).filter(([key]) => shown[key]);
  const splits = can.seeOtherSplits ? allSplitsFor(shown, agents) : [];
  const ownSplit = ownSplitFor(shown, identity);

  const handleStatusChanged = (updated) => {
    setStatusOverride(updated);
    onSaved(); // refresh the book so Book/hub lists reflect the new status
  };

  return (
    <>
      <Drawer
        open={!editing}
        onClose={onClose}
        title={`${shown.lob} · ${shown.carrier || "Unknown carrier"}`}
        footer={
          can.editRecords ? (
            <>
              {/* Reactivate is the undo for a mis-click (Session I T4). */}
              <PolicyStatusAction policy={shown} onChanged={handleStatusChanged} />
              <Button variant="primary" onClick={() => setEditing(true)}>
                Edit policy
              </Button>
            </>
          ) : null
        }
      >
        <div className="feat-kv">
          <KvRow label="Status">
            <PolicyStatusBadge policy={shown} />
          </KvRow>
          <KvRow label="Policy number">{shown.policyNumber || "—"}</KvRow>
          <KvRow label="Carrier">{shown.carrier || "—"}</KvRow>
          <KvRow label="Effective">{formatDate(shown.effectiveDate)}</KvRow>
          <KvRow label="Renews">
            <RenewalBadge renewal={renewal} />
          </KvRow>
          <KvRow label="Premium">{formatMoney(shown.premium)}/yr</KvRow>
        </div>

        {can.seePolicyCoverages && coverage.length > 0 ? (
          <div className="feat-kv-section">
            <h3 className="feat-kv-section__title">Coverages</h3>
            <div className="feat-kv">
              {coverage.map(([key, label]) => (
                <KvRow key={key} label={label}>
                  {MONEYISH.has(key) ? formatMoney(shown[key]) : shown[key]}
                </KvRow>
              ))}
            </div>
          </div>
        ) : null}

        {can.seeOtherSplits && splits.length > 0 ? (
          <div className="feat-kv-section">
            <h3 className="feat-kv-section__title">Commission splits</h3>
            <div className="feat-kv">
              {splits.map((s, i) => (
                <KvRow key={i} label={s.name}>
                  {s.split == null ? "—" : `${s.split}%`}
                  <span className="fk-muted"> · {s.label}</span>
                </KvRow>
              ))}
            </div>
          </div>
        ) : null}

        {!can.seeOtherSplits && ownSplit != null ? (
          <div className="feat-kv-section">
            <h3 className="feat-kv-section__title">Your commission</h3>
            <div className="feat-kv">
              <KvRow label="Your split">
                <Badge variant="brand">{ownSplit}%</Badge>
              </KvRow>
              <KvRow label="Est. annual commission">
                {formatMoney((Number(shown.premium) || 0) * COMMISSION_RATE * (ownSplit / 100))}
                <span className="fk-muted"> est.</span>
              </KvRow>
            </div>
          </div>
        ) : null}
      </Drawer>

      {can.editRecords ? (
        <PolicyEditDrawer
          policy={editing ? shown : null}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            onSaved();
          }}
        />
      ) : null}
    </>
  );
}

export default PolicyDrawer;
